import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { useFaust } from "../../contexts/FaustContext";
import { PROJECT_DIR } from "../../contexts/FaustContext";
import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { ExportDialog } from "../dialogs/ExportDialog";
import { ShareDialog } from "../dialogs/ShareDialog";
import {
    Play,
    Flame,
    Share2,
    Upload,
    Save,
    BookOpen,
    Loader2,
} from "lucide-react";
import { cn } from "../../lib/utils";

export function ActionButtons() {
    const { state, dispatch, runDsp, editorRef, libFaustRef } = useFaust();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [exportOpen, setExportOpen] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);

    const handleRun = useCallback(async () => {
        const editor = editorRef.current;
        if (!editor) return;
        const result = await runDsp(editor.getValue());
        if (result.success) {
            dispatch({ type: "SET_ACTIVE_BOTTOM_TAB", tab: "dsp" });
        }
    }, [runDsp, editorRef, dispatch]);

    const handleUpload = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files;
            if (!files?.length) return;

            Array.from(files).forEach((file) => {
                const fileName = file.name.replace(/[^a-zA-Z0-9_.]/g, "") || "untitled";
                const isDsp = fileName.endsWith(".dsp");
                const isBinary = fileName.endsWith(".wav") || fileName.endsWith(".flac") || fileName.endsWith(".mp3");

                const reader = new FileReader();
                reader.onload = () => {
                    const libFaust = libFaustRef.current;
                    if (!libFaust) return;
                    const fs = libFaust.fs();

                    if (isDsp) {
                        // DSP files: write to project dir and load into editor
                        const content = reader.result as string;
                        fs.writeFile(PROJECT_DIR + fileName, content);
                        editorRef.current?.setValue(content);
                        toast.success(`Loaded ${fileName}`);
                    } else if (isBinary) {
                        // Binary audio files: write as Uint8Array
                        const content = new Uint8Array(reader.result as ArrayBuffer);
                        fs.writeFile(PROJECT_DIR + fileName, content);
                        toast.success(`Added ${fileName} to project`);
                    } else {
                        // Text files (.lib, etc): write as string
                        const content = reader.result as string;
                        fs.writeFile(PROJECT_DIR + fileName, content);
                        toast.success(`Added ${fileName} to project`, {
                            description: fileName.endsWith(".lib")
                                ? `Use: import("${fileName}");`
                                : undefined,
                        });
                    }
                };

                if (isBinary) {
                    reader.readAsArrayBuffer(file);
                } else {
                    reader.readAsText(file);
                }
            });

            e.target.value = "";
        },
        [editorRef, libFaustRef]
    );

    const handleSave = useCallback(async () => {
        const editor = editorRef.current;
        if (!editor) return;
        const code = editor.getValue();
        const blob = new Blob([code], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "untitled.dsp";
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
    }, [editorRef]);

    const handleDocs = useCallback(() => {
        window.open("https://faust.grame.fr/tools/editor/libraries/doc/library.html", "_blank");
    }, []);

    return (
        <div className="flex flex-col gap-1.5">
            {/* Run - Primary action */}
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        variant="outline"
                        className={cn(
                            "w-full justify-start gap-2 h-9",
                            "btn-run font-medium"
                        )}
                        onClick={handleRun}
                        disabled={!state.isReady || state.isCompiling}
                    >
                        {state.isCompiling ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Play className="h-3.5 w-3.5" />
                        )}
                        Run
                    </Button>
                </TooltipTrigger>
                <TooltipContent side="right">Ctrl+R - Compile and run</TooltipContent>
            </Tooltip>

            {/* Export - Default target */}
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        variant="outline"
                        className="w-full justify-start gap-2 h-9 btn-export-action"
                        disabled={!state.isReady}
                        onClick={() => setExportOpen(true)}
                    >
                        <Flame className="h-3.5 w-3.5" />
                        Export
                    </Button>
                </TooltipTrigger>
                <TooltipContent side="right">Export to platform</TooltipContent>
            </Tooltip>

            {/* Share */}
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        variant="outline"
                        className="w-full justify-start gap-2 h-9 btn-share-action"
                        onClick={() => setShareOpen(true)}
                    >
                        <Share2 className="h-3.5 w-3.5" />
                        Share
                    </Button>
                </TooltipTrigger>
                <TooltipContent side="right">Share your code</TooltipContent>
            </Tooltip>

            {/* Small buttons row */}
            <div className="grid grid-cols-3 gap-1.5">
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs btn-upload-action px-1"
                            onClick={handleUpload}
                        >
                            <Upload className="h-3 w-3" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">Upload file</TooltipContent>
                </Tooltip>

                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs btn-save-action px-1"
                            onClick={handleSave}
                        >
                            <Save className="h-3 w-3" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">Save as file</TooltipContent>
                </Tooltip>

                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs btn-docs-action px-1"
                            onClick={handleDocs}
                        >
                            <BookOpen className="h-3 w-3" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">Ctrl+D - Documentation</TooltipContent>
                </Tooltip>
            </div>

            {/* Hidden file input -- accepts .dsp, .lib, .wav, .flac, etc. */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".dsp,.lib,.wav,.flac,.mp3"
                className="hidden"
                onChange={handleFileChange}
            />

            {/* Dialogs */}
            <ExportDialog open={exportOpen} onOpenChange={setExportOpen} />
            <ShareDialog open={shareOpen} onOpenChange={setShareOpen} />
        </div>
    );
}
